import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    inject,
    OnInit,
    Output,
    signal,
    viewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { minutesPerHour } from '../../../consts/time.const';
import { formatDurationMinutes } from '../../../utils/format.utils';

/** the largest minute value that is not a whole hour */
const maxMinuteValue = 59;

/** digits an hours or minutes box accepts before it is full */
const inputMaxLength = 2;

/**
 * Hours/minutes dialog behind `DurationStepperFieldComponent`.
 *
 * Speaks the same currency as the field that opens it — **total minutes** — so no
 * `DurationModel` is constructed anywhere along the way. That is the whole reason this
 * exists next to the reactive-forms `DurationManualInputComponent` rather than reusing
 * it: adapting to and from a model class on every open was the last place the new
 * duration path still depended on the legacy representation.
 *
 * Opened imperatively through `BsModalService`, so the inbound contract is plain
 * writable properties (`initialState` assigns straight onto the instance, which would
 * clobber signal inputs) and the outbound one an `EventEmitter` the caller can pipe.
 * The internals are plain signals rather than a signal form: nothing here edits store
 * state, so a schema would be ceremony around two boxes and one range rule.
 */
@Component({
    selector: 'pace-duration-manual-input-modal',
    templateUrl: './duration-manual-input-modal.component.html',
    styleUrls: ['./duration-manual-input-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule],
})
export class DurationManualInputModalComponent implements OnInit, AfterViewInit {
    // --- inbound contract, set by `initialState` ------------------------------

    /** the duration the dialog opens with, in total minutes; `undefined` to start empty */
    public durationInMinutes: number | undefined = undefined;

    /** inclusive bounds in total minutes */
    public minMinutes = 0;
    public maxMinutes: number | undefined = undefined;

    // --- outbound contract ---------------------------------------------------

    /** the accepted duration, in total minutes */
    @Output()
    public readonly durationChanged = new EventEmitter<number>();

    public readonly modalInstance = inject(BsModalRef);

    public readonly maxMinuteValue = maxMinuteValue;

    protected readonly hours = signal<number | undefined>(undefined);
    protected readonly minutes = signal<number | undefined>(undefined);

    protected readonly totalMinutes = computed(() => (this.hours() ?? 0) * minutesPerHour + (this.minutes() ?? 0));

    /** both boxes have to carry a number before the duration means anything */
    protected readonly isComplete = computed(() => this.hours() !== undefined && this.minutes() !== undefined);

    protected readonly isMinutesOutOfRange = computed(() => {
        const minutes = this.minutes();

        return minutes !== undefined && (minutes < 0 || minutes > maxMinuteValue);
    });

    /** range messages, mirroring the reactive-forms dialog's form-level errors */
    protected readonly errorMessages = computed(() => {
        if (!this.isComplete() || this.isMinutesOutOfRange()) {
            return [];
        }

        const total = this.totalMinutes();
        const messages: string[] = [];
        const max = this.maxMinutes;

        if (max !== undefined && total > max) {
            messages.push(
                <string>this.translateService.instant('DURATION_MANUAL_INPUT_MAX_ERROR', {
                    duration: formatDurationMinutes(total),
                    maxDuration: formatDurationMinutes(max),
                }),
            );
        }
        if (total < this.minMinutes) {
            messages.push(
                <string>this.translateService.instant('DURATION_MANUAL_INPUT_MIN_ERROR', {
                    duration: formatDurationMinutes(total),
                    minDuration: formatDurationMinutes(this.minMinutes),
                }),
            );
        }

        return messages;
    });

    protected readonly isInvalid = computed(
        () => !this.isComplete() || this.isMinutesOutOfRange() || this.errorMessages().length > 0,
    );

    private readonly hoursInput = viewChild.required<ElementRef<HTMLInputElement>>('hoursInput');
    private readonly minutesInput = viewChild.required<ElementRef<HTMLInputElement>>('minutesInput');

    private readonly translateService = inject(TranslateService);

    public ngOnInit(): void {
        // an empty field opens with empty boxes rather than 00:00, so the user is not
        // asked to clear a value they never entered
        if (this.durationInMinutes === undefined) {
            return;
        }

        this.hours.set(Math.floor(this.durationInMinutes / minutesPerHour));
        this.minutes.set(this.durationInMinutes % minutesPerHour);
    }

    public ngAfterViewInit(): void {
        // deferred so the focus lands after the modal's own opening animation
        setTimeout(() => this.hoursInput().nativeElement.focus(), 0);
    }

    public onHoursInput(event: Event): void {
        const input = <HTMLInputElement>event.target;
        this.hours.set(this.readNumber(input));

        // jump on once the box is full, as the reactive-forms dialog does
        if (input.value.length >= inputMaxLength) {
            setTimeout(() => this.minutesInput().nativeElement.focus(), 0);
        }
    }

    public onMinutesInput(event: Event): void {
        this.minutes.set(this.readNumber(<HTMLInputElement>event.target));
    }

    public save(): void {
        if (this.isInvalid()) {
            return;
        }

        this.durationChanged.emit(this.totalMinutes());
        this.modalInstance.hide();
    }

    /**
     * @param input the box that was edited
     * @returns its value, or undefined while it is empty or unparseable
     */
    private readNumber(input: HTMLInputElement): number | undefined {
        const parsed = input.valueAsNumber;

        return Number.isNaN(parsed) ? undefined : parsed;
    }
}
